import React, { useState } from 'react';
import StressTestChart from './StressTestChart';
import StressTestTable from './StressTestTable';
import { buildDisplayNameMap } from '../utils/portfolioNames';

const StressTestSummary = ({ stressTestResults, perPortfolioStress, portfolioRegistry }) => {
    const nameMap = buildDisplayNameMap(portfolioRegistry);
    const displayName = (code) => nameMap[code] || code;

    if (!perPortfolioStress || Object.keys(perPortfolioStress).length === 0) {
        return (
            <div className="text-center text-muted py-5">
                No per-portfolio stress data available
            </div>
        );
    }

    // Get portfolio names from first event (excluding 'Blend')
    const firstEvent = Object.keys(perPortfolioStress)[0];
    const allKeys = Object.keys(perPortfolioStress[firstEvent] || {});
    const portfolioNames = allKeys.filter((k) => k !== 'Blend');

    // Use filtered results to match the events shown in other views
    const filteredResults = stressTestResults.filter(
        (event) => event.top_contributors.length > 0 || event.portfolio_return !== 0
    );

    return (
        <div style={{ overflowX: 'auto' }}>
            <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.85rem' }}>
                <thead style={{ backgroundColor: '#072040', color: '#fff' }}>
                    <tr>
                        <th>Event</th>
                        {portfolioNames.map((name) => (
                            <th key={name} className="text-end">
                                {displayName(name)}
                            </th>
                        ))}
                        <th
                            className="text-end"
                            style={{ backgroundColor: '#f0a500', color: '#072040', fontWeight: 700 }}
                        >
                            Blend
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {filteredResults.map((event, idx) => {
                        const evtData = perPortfolioStress[event.event_name];
                        if (!evtData) return null;
                        return (
                            <tr key={idx}>
                                <td style={{ whiteSpace: 'nowrap' }}>{event.event_name}</td>
                                {portfolioNames.map((name) => {
                                    const val = evtData[name];
                                    return (
                                        <td
                                            key={name}
                                            className="text-end"
                                            style={{
                                                color:
                                                    val != null
                                                        ? val >= 0
                                                            ? '#198754'
                                                            : '#dc3545'
                                                        : undefined,
                                            }}
                                        >
                                            {val != null ? (val * 100).toFixed(2) : '-'}
                                        </td>
                                    );
                                })}
                                <td
                                    className="text-end"
                                    style={{
                                        fontWeight: 600,
                                        backgroundColor: '#E7E6E6',
                                        color: evtData.Blend >= 0 ? '#198754' : '#dc3545',
                                    }}
                                >
                                    {evtData.Blend != null ? (evtData.Blend * 100).toFixed(2) : '-'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const StressTest = React.forwardRef(({ stressTestResults, perPortfolioStress, weights, portfolioRegistry }, ref) => {
    const [viewMode, setViewMode] = useState('chart');

    if (!stressTestResults || stressTestResults.length === 0) {
        return null;
    }

    // Count how many portfolios have weight > 0
    const activePortfolios = weights
        ? Object.values(weights).filter((w) => w > 0).length
        : 0;
    const showSummary = activePortfolios > 1;

    return (
        <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                    <h5 className="mb-0">Historical Stress Testing</h5>
                    <small className="text-muted">Portfolio performance during major market events</small>
                </div>
                <div className="btn-group btn-group-sm">
                    <button
                        className={`btn ${viewMode === 'chart' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('chart')}
                        style={viewMode === 'chart' ? { backgroundColor: '#072040', borderColor: '#072040' } : { color: '#072040', borderColor: '#072040' }}
                    >
                        Chart
                    </button>
                    <button
                        className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('table')}
                        style={viewMode === 'table' ? { backgroundColor: '#072040', borderColor: '#072040' } : { color: '#072040', borderColor: '#072040' }}
                    >
                        Table
                    </button>
                    {showSummary && (
                        <button
                            className={`btn ${viewMode === 'summary' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setViewMode('summary')}
                            style={viewMode === 'summary' ? { backgroundColor: '#072040', borderColor: '#072040' } : { color: '#072040', borderColor: '#072040' }}
                        >
                            Summary
                        </button>
                    )}
                </div>
            </div>
            <div className="card-body" style={viewMode === 'chart' ? { height: '500px' } : {}}>
                {viewMode === 'chart' ? (
                    <StressTestChart ref={ref} stressTestResults={stressTestResults} portfolioRegistry={portfolioRegistry} />
                ) : viewMode === 'summary' ? (
                    <StressTestSummary
                        stressTestResults={stressTestResults}
                        perPortfolioStress={perPortfolioStress}
                        portfolioRegistry={portfolioRegistry}
                    />
                ) : (
                    <StressTestTable stressTestResults={stressTestResults} portfolioRegistry={portfolioRegistry} />
                )}
            </div>
        </div>
    );
});

export default StressTest;
